import { format } from 'date-fns'

import { cancelBooking } from '@/helpers/externals/bookings'
import { removeBookingItem } from '@/helpers/externals/cart'

const state = {
  cartId: null,
  promocode: null,
  cart: [],
  lastBookingsRefreshed: null,
  proposedDropoffTimes: [],
  proposedPickupTimes: [],
  creditsPack: [],
  backendCart: null,
}

const getters = {
  getCreditsPack: state => [...state.creditsPack],
  getCartId: state => state.cartId,
  getPromocodeId: state => state.promocode !== null ? state.promocode.pk : null,
  getPromocode: state => state.promocode,
  getBackendCart: state => state.backendCart,
  getCart: state => [...state.cart],
  getDate: state => {
    const cartItems = state.cart.filter(cartItem => cartItem.booking && cartItem.booking.pickupTime)
    const dates = [...new Set(cartItems.map(cartItem => format(cartItem.booking.pickupTime, 'yyyy-MM-dd')))]
    if (dates.length > 1) {
      return dates
    } else if (dates.length === 1) {
      return dates[0]
    } else {
      return null
    }
  },
  getLastBookingsRefreshed: state => state.lastBookingsRefreshed,
  getProposedDropoffTimes: state => state.proposedDropoffTimes,
  getProposedPickupTimes: state => state.proposedPickupTimes
}

const mutations = {
  setBackendCart(state, payload) {
    state.backendCart = {
      id: payload.pk.toString(),
      discount_incl_vat: payload.fields.discount_incl_vat,
      price_discount_incl_vat: payload.fields.price_discount_incl_vat,
      sum_price_incl_vat: payload.fields.sum_price_incl_vat,
      promocode: payload.fields.promocode,
    }

  },
  addVehicleToCart(state, vehicleId) {
    if (!state.cart.some(element => element.vehicleId === vehicleId)) {
      state.cart.push({ vehicleId })
    }
  },
  removeVehicleFromCart(state, vehicleId) {
    state.cart = state.cart.filter(cartItem => {
      return cartItem.vehicleId !== vehicleId
    })
  },
  replaceVehicleFromCart(state, payload) {
    state.cart.splice(payload.position, 1, { vehicleId: payload.vehicleId })
  },
  addOfferToVehicle(state, payload) {
    state.cart = state.cart.map(cartItem => {
      if (cartItem.vehicleId === payload.vehicleId) {
        return {
          ...cartItem,
          offerId: payload.offerId,
        }
      }
      return cartItem
    })
  },
  addAddonsToVehicle(state, payload) {
    state.cart = state.cart.map(cartItem => {
      if (cartItem.vehicleId === payload.vehicleId) {
        return {
          ...cartItem,
          addons: payload.addons,
        }
      }
      return cartItem
    })
  },
  clearCart(state) {
    state.cartId = null
    state.cart = []
    state.lastBookingsRefreshed = null
    state.proposedDropoffTimes = []
    state.promocode = null
    state.backendCart = null
  },
  clearCreditsPack(state) {
    state.creditsPack = []
  },
  setLastBookingsRefreshed(state, value) {
    state.lastBookingsRefreshed = value
  },
  setBookingId(state, { bookingId, vehicleId }) {
    const cartItem = state.cart.find(cartItem => cartItem.vehicleId === vehicleId)
    if (cartItem) {
      cartItem.booking = {
        bookingId
      }
    }
  },
  setCreditsPackId(state, { pk, text }) {
    const cartCreditsPackIndex = state.creditsPack.findIndex(cartItem => cartItem.creditsPackId)
    if (cartCreditsPackIndex === -1) {
      state.creditsPack.push({
        creditsPackId: pk,
        text: text
      })
    } else {
      state.creditsPack.splice(cartCreditsPackIndex, 1, { creditsPackId: pk, text: text })
    }
  },
  setDropoffTime(state, { bookingId, dropoffTime }) {
    const cartIndex = state.cart.findIndex(cartItem => {
      if (cartItem.booking) {
        return cartItem.booking.bookingId === bookingId
      }
      return false
    })
    // use splice to enable reactivity with getDate
    if (cartIndex !== -1) {
      state.cart.splice(cartIndex, 1, {
        ...state.cart[cartIndex],
        booking: {
          ...state.cart[cartIndex].booking,
          dropoffTime
        }
      })
    }
  },
  setPickupTime(state, { bookingId, pickupTime }) {
    const cartIndex = state.cart.findIndex(cartItem => {
      if (cartItem.booking) {
        return cartItem.booking.bookingId === bookingId
      }
      return false
    })
    if (cartIndex !== -1) {
      state.cart.splice(cartIndex, 1, {
        ...state.cart[cartIndex],
        booking: {
          ...state.cart[cartIndex].booking,
          pickupTime
        }
      })
    }
  },
  setProposedDropoffTimes(state, payload) {
    state.proposedDropoffTimes = payload
  },
  setProposedPickupTimes(state, payload) {
    state.proposedPickupTimes = payload
  },
  setPromocode(state, payload) {
    state.promocode = payload
  },
  clearPromocode(state) {
    state.promocode = null
  },
  setCartId(state, payload) {
    state.cartId = payload
  },
  setBookingCartItemId(state, { bookingId, cartItemId }) {
    const cartIndex = state.cart.findIndex(cartItem => {
      if (cartItem.booking) {
        return cartItem.booking.bookingId === bookingId
      }
      return false
    })
    if (cartIndex !== -1) {
      state.cart.splice(cartIndex, 1, {
        ...state.cart[cartIndex],
        cartItemId
      })
    }
  },
  setCreditsPackCartItemId(state, { creditsPackId, cartItemId }) {
    const cartIndex = state.creditsPack.findIndex(cartItem => {
      if (cartItem.creditsPackId) {
        return cartItem.creditsPackId === creditsPackId
      }
      return false
    })
    if (cartIndex !== -1) {
      state.creditsPack.splice(cartIndex, 1, {
        ...state.creditsPack[cartIndex],
        cartItemId
      })
    }
  }
}

const actions = {
  addVehicleToCart({ commit }, vehicleId) {
    commit('addVehicleToCart', vehicleId)
  },
  removeVehicleFromCart({ commit, state }, vehicleId) {
    const cartItem = state.cart.find(cartItem => cartItem.vehicleId === vehicleId)
    if (state.cartId && cartItem.cartItemId) {
      removeBookingItem(state.cartId, cartItem.cartItemId)
    }
    if (cartItem.booking) {
      cancelBooking(cartItem.booking.bookingId)
    }
    commit('removeVehicleFromCart', vehicleId)
  },
  replaceVehicleFromCart({ commit }, payload) {
    commit('replaceVehicleFromCart', payload)
  },
  addOfferToVehicle({ commit }, payload) {
    commit('addOfferToVehicle', payload)
  },
  addAddonsToVehicle({ commit }, payload) {
    commit('addAddonsToVehicle', payload)
  },
  clearCart({ commit }) {
    commit('clearCart')
  },
  clearPromocode({ commit }) {
    commit('clearPromocode')
  },
  clearCreditsPack({ commit }) {
    commit('clearCreditsPack')
  },
  setPickupTime({ commit }, payload) {
    commit('setPickupTime', payload)
  },
  setCreditsPackId({ commit }, payload) {
    commit('setCreditsPackId', payload)
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
